import React, { useState, useEffect } from 'react';
import { MoreVertical, Pencil } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatTimeRemaining } from '@/lib/dateFormat';
import { formatOrdinalDate } from '@/utils/formatOrdinalDate';
import { CountdownIcon } from '@/components/icons/CountdownIcon';
import { LiveIcon } from '@/components/icons/LiveIcon';
import { CompleteIcon } from '@/components/icons/CompleteIcon';
import { AdSlot } from '@/components/ads/AdSlot';
import { usePickemContestCardAd } from '@/hooks/usePickemContestCardAd';
import { Button } from '../ui/button';
import { LeaderBoardIcon } from '../icons/LeaderBoardIcon';
import { ViewResultsIcon } from '../icons/ViewResultsIcon';

type GameState = 'upcoming' | 'countdown' | 'live' | 'final';

interface GameCardProps {
  id: string;
  title: string;
  prize: string;
  state: GameState;
  gameDate: Date;
  finalScore?: { rank: number; points: number };
  hasSelections?: boolean;
  /** Whether the user has reserved a seat (DKPLAT-528) without submitting picks yet. */
  hasReservation?: boolean;
  /**
   * Whether this pickem has any playable questions posted yet. Derived from
   * the real questions array (see type-transformers.ts) — NOT from
   * questionsPostedAt, which stays set forever once questions post and is
   * display-only for the "Questions will be posted <date>" label.
   */
  hasQuestions?: boolean;
  /** ISO timestamp for when questions were/will be posted, or null. Display-only. */
  questionsPostedAt?: string | null;
  isLocked?: boolean;
  isOpen?: boolean; // Whether the pickem is open for entries
  totalPicks?: number;
  correctPicks?: number;
  currentPoints?: number;
  leagueLogoUrl?: string;
  totalEntries?: number;
  entryCost?: string;
  onEdit?: () => void;
  onPlay?: () => void;
  onView?: () => void;
  onViewLeaderboard?: () => void;
  onMenuClick?: () => void;
  className?: string;
}

const GameCard: React.FC<GameCardProps> = ({
  id,
  title,
  prize,
  state,
  gameDate,
  finalScore,
  hasSelections = false,
  hasReservation = false,
  // DKPLAT-533: defaults to FALSE — hasQuestions is only ever read on the reserved
  // paths below, where an absent signal must fail closed ("Entry Reserved"), never
  // open onto an enabled "Make Selections Now!" Edit. Same fail-open class as the
  // squares bug this ticket fixes.
  hasQuestions = false,
  questionsPostedAt = null,
  isLocked = false,
  isOpen = true,
  totalPicks = 6,
  correctPicks,
  currentPoints,
  leagueLogoUrl,
  totalEntries,
  entryCost,
  onEdit,
  onPlay,
  onView,
  onViewLeaderboard,
  onMenuClick,
  className = '',
}) => {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<string>('');
  const { adPlacement, shouldShowAd } = usePickemContestCardAd(id);

  // DKPLAT-528: hasQuestions is the real "has playable questions" signal
  // (derived upstream from the questions array). questionsPostedAt is
  // display-only for the date label below.
  const questionsPostedLabel = formatOrdinalDate(questionsPostedAt);

  // DKPLAT-533 three-state matrix for a reservation that never converted to picks.
  // Mirrors apps/squares ContestCard so both games read identically:
  //   1. reserved + upcoming + questions NOT available → "Entry Reserved"
  //   2. reserved + upcoming + questions available     → "No Picks Submitted -
  //      Make Selections Now!" (prominent — user action required)
  //   3. reserved + locked/live + no selections        → "No Picks Submitted"
  //      (persists into Past Results after grading)
  const isReservedUnconverted = hasReservation && !hasSelections;
  const isPreLive = state === 'countdown' || state === 'upcoming';
  const showReservedRow = isPreLive && isReservedUnconverted && isOpen;
  const showNoPicksLabel = (state === 'live' || state === 'final') && isReservedUnconverted;

  useEffect(() => {
    // Only set up interval for countdown state
    if (state !== 'countdown') {
      return;
    }

    // Initial calculation
    setTimeRemaining(formatTimeRemaining(gameDate));

    const interval = setInterval(() => {
      const now = new Date();
      const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;

      // Check if countdown has expired
      if (targetDate.getTime() <= now.getTime()) {
        clearInterval(interval);
        setTimeRemaining(t('game.time.startingSoon'));
      } else {
        setTimeRemaining(formatTimeRemaining(targetDate));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gameDate, state, t]);

  const getStartDisplay = () => {
    switch (state) {
      case 'countdown':
        return timeRemaining || formatTimeRemaining(gameDate);
      case 'live':
        return t('game.status.live');
      case 'final':
        return t('game.status.complete');
      default: {
        // Calculate relative time for upcoming
        const now = new Date();
        const targetDate = typeof gameDate === 'string' ? new Date(gameDate) : gameDate;
        const diff = targetDate.getTime() - now.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        if (days > 0) return `${days} ${days > 1 ? t('game.time.days') : t('game.time.day')}`;
        const hours = Math.floor(diff / (1000 * 60 * 60));
        if (hours > 0) return `${hours} ${hours > 1 ? t('game.time.hours') : t('game.time.hour')}`;
        return t('game.time.soon');
      }
    }
  };

  const getStartIcon = () => {
    switch (state) {
      case 'countdown':
      case 'upcoming':
        return <CountdownIcon className="w-4 h-4 flex-shrink-0" />;
      case 'live':
        return <LiveIcon className="w-4 h-4 flex-shrink-0" />;
      case 'final':
        return <CompleteIcon className="w-4 h-4 flex-shrink-0" />;
      default:
        return <CountdownIcon className="w-4 h-4 flex-shrink-0" />;
    }
  };

  const getPicksDisplay = () => {
    // For final state with selections, show correct/total ratio
    if (state === 'final' && correctPicks !== undefined) {
      return `${correctPicks}/${totalPicks}`;
    }
    // For final state without selections, show -/total
    if (state === 'final' && !hasSelections) {
      return `-/${totalPicks}`;
    }
    // For live state with selections and correct picks data
    if (state === 'live' && hasSelections && correctPicks !== undefined) {
      return `${correctPicks}/${totalPicks} ${t('game.status.correct')}`;
    }
    // Show total picks for upcoming and countdown
    return totalPicks.toString();
  };

  const getOrdinalSuffix = (rank: number): string => {
    const j = rank % 10;
    const k = rank % 100;
    if (j === 1 && k !== 11) return t('game.ordinals.st');
    if (j === 2 && k !== 12) return t('game.ordinals.nd');
    if (j === 3 && k !== 13) return t('game.ordinals.rd');
    return t('game.ordinals.th');
  };

  // Handle card click based on state
  const handleCardClick = () => {
    // Don't allow clicks if pickem is not open
    if (!isOpen) {
      return;
    }

    if (isLocked) {
      onView?.();
    } else if (state === 'live' || state === 'final') {
      onView?.();
    } else if (hasSelections) {
      onEdit?.();
    } else {
      onPlay?.();
    }
  };

  const getCardBackground = () => {
    return 'bg-[#242424]';
  };

  // Determine if this is a winner card (1st place)
  const isWinner = state === 'final' && finalScore?.rank === 1;

  return (
    <div
      data-test-id={`game-card-${id}`}
      data-test-state={state}
      data-test-locked={isLocked}
      onClick={handleCardClick}
      className={`
        rounded-xl p-4
        border border-[#373737]
        ${getCardBackground()}
        ${isWinner ? 'winner-card-glow' : ''}
        ${isLocked ? 'opacity-60' : ''}
        ${!isOpen ? 'cursor-default opacity-50' : 'cursor-pointer hover:ring-2 hover:ring-draftkings-primary/30 active:scale-[0.98]'}
        transition-all duration-200
        ${className}
      `}
    >
      {/* Row 1: Top Row */}
      <div className="flex items-center justify-between mb-4">
        {/* Left: League logo + Title with subtitle */}
        <div className="flex items-center gap-3">
          <img
            src={leagueLogoUrl || '/league-icon.svg'}
            alt="League icon"
            className="w-10 h-10 object-contain flex-shrink-0"
          />
          <div className="flex flex-col gap-0.5">
            <span
              data-test-id={`game-title-${id}`}
              className="font-gilroy font-bold text-xs text-text-primary tracking-wide uppercase leading-tight"
            >
              {title}
            </span>
            <span className="text-xs font-gilroy font-semibold">
              <span className="text-[#ABABAB]">Start: </span>
              <span className="text-pickem-primary">{getStartDisplay()}</span>
              <span className="text-[#ABABAB]">
                {' '}
                ({totalPicks} {totalPicks === 1 ? 'Game' : 'Games'})
              </span>
            </span>
          </div>
        </div>

        {/* Right: Three-dots menu */}
        <button
          onClick={e => {
            e.stopPropagation();
            onMenuClick?.();
          }}
          className="p-2 rounded-lg hover:bg-app-surface-hover transition-smooth"
          aria-label="More options"
        >
          <MoreVertical className="w-5 h-5 text-text-secondary" />
        </button>
      </div>

      {/* Pickem-specific Google Ad */}
      {shouldShowAd && adPlacement && (
        <div className="mb-4">
          <AdSlot
            divId={`pickem-contest-card-${id}`}
            adPlacement={adPlacement}
            placement="pickem-contest-card"
            className="rounded-lg overflow-hidden"
          />
        </div>
      )}

      {/* Horizontal Rule */}
      <div className="border-t-2 border-border/20 mb-4"></div>

      {/* Row 2: Bottom Row - Three Columns */}
      <div className="grid grid-cols-3 gap-4">
        {/* Column 1: Entries (left aligned) */}
        <div className="flex flex-col gap-1 text-left border-r border-[#6F6F6F] pr-4">
          <span className="text-[10px] text-text-muted font-gilroy">Entries</span>
          <span className="text-sm font-gilroy font-bold text-text-primary">
            {totalEntries?.toLocaleString() || '0'}
          </span>
        </div>

        {/* Column 2: Entry (center aligned) */}
        <div className="flex flex-col gap-1 text-center border-r border-[#6F6F6F] pr-4">
          <span className="text-[10px] text-text-muted font-gilroy">Entry</span>
          <span
            data-test-id={`entry-cost-${id}`}
            className="text-sm font-gilroy font-bold text-text-primary"
          >
            {entryCost || 'FREE'}
          </span>
        </div>

        {/* Column 3: Prize Pool (right aligned) */}
        <div className="flex flex-col gap-1 text-right">
          <span className="text-[10px] text-text-muted font-gilroy">Prize Pool</span>
          <span
            data-test-id={`prize-${id}`}
            className="text-sm font-gilroy font-bold text-pickem-primary"
          >
            {prize}
          </span>
        </div>
      </div>

      {/* Reserved-but-not-yet-open (DKPLAT-528): questions haven't been posted yet.
          DKPLAT-533: scoped to the pre-live reserved row so it can't linger on a
          locked/live card underneath the "No Picks Submitted" label. */}
      {showReservedRow && !hasQuestions && questionsPostedLabel && (
        <p
          data-test-id={`reserved-questions-posted-${id}`}
          className="mt-3 text-xs font-gilroy font-semibold text-yellow-500 text-center"
        >
          Questions will be posted {questionsPostedLabel}
        </p>
      )}

      {/* Action buttons for completed/live contests */}
      {(state === 'final' || state === 'live') && (
        <div className="mt-4 flex gap-2 justify-between w-full">
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onView?.();
            }}
            className="rounded-full border border-border bg-app-background text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-3 sm:px-4"
            data-test-id={`view-results-${id}`}
          >
            {t('game.actions.viewResults')}
            <ViewResultsIcon />
          </Button>
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onViewLeaderboard?.();
            }}
            className="rounded-full border border-border bg-app-background text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-3 sm:px-4"
            data-test-id={`view-leaderboard-${id}`}
          >
            {t('game.actions.viewLeaderboard')}
            <LeaderBoardIcon />
          </Button>
        </div>
      )}

      {/* Reserved-seat row (DKPLAT-528): shown while the entry is a reservation
          with no picks yet. Edit is disabled until questions post, then enables
          and drives the normal picks flow. Max Entered shows a filled/
          "successful" disabled state (can't enter again — a reservation already
          counts as the one entry). Once picks are
          submitted (hasSelections), the normal single Edit Picks button below
          takes over. */}
      {/* DKPLAT-533 states 1 & 2 — the status text above the reserved row. Classes
          mirror apps/squares ContestCard so the two games read identically. */}
      {showReservedRow && (
        <p
          data-test-id={
            hasQuestions ? `reserved-make-selections-${id}` : `reserved-entry-reserved-${id}`
          }
          className={
            hasQuestions
              ? 'mt-3 text-sm font-gilroy font-bold text-[#53d337] text-center'
              : 'mt-3 text-xs font-gilroy font-semibold text-yellow-500 text-center'
          }
        >
          {hasQuestions ? 'No Picks Submitted - Make Selections Now!' : 'Entry Reserved'}
        </p>
      )}

      {/* DKPLAT-533 state 3 — once the contest locks with the reservation still
          unconverted, "No Picks Submitted" persists through live → Past Results. */}
      {showNoPicksLabel && (
        <p
          data-test-id={`reserved-no-picks-submitted-${id}`}
          className="mt-3 text-xs font-gilroy font-semibold text-yellow-500 text-center"
        >
          No Picks Submitted
        </p>
      )}

      {showReservedRow && (
        <div className="mt-4 flex gap-2 justify-between w-full">
          <Button
            variant="outline"
            disabled={!hasQuestions}
            onClick={e => {
              e.stopPropagation();
              onEdit?.();
            }}
            className={`rounded-full border border-border bg-app-background whitespace-nowrap px-3 flex-1 ${
              hasQuestions
                ? 'text-foreground hover:bg-app-surface-hover hover:text-foreground'
                : 'text-foreground/50 cursor-not-allowed'
            }`}
            data-test-id={
              hasQuestions ? `reserved-edit-picks-${id}` : `reserved-edit-picks-disabled-${id}`
            }
          >
            {t('game.actions.editPicks')} (1)
          </Button>
          <Button
            variant="pickem"
            disabled
            className="whitespace-nowrap px-3 flex-1 cursor-not-allowed opacity-90"
            data-test-id={`reserved-max-entered-${id}`}
          >
            Max Entered
          </Button>
        </div>
      )}

      {/* Edit Picks button for upcoming/countdown contests with existing selections.
          Mutually exclusive with the reserved-seat row above: a reservation that
          has converted to real picks (hasSelections) renders this normal path. */}
      {(state === 'countdown' || state === 'upcoming') && hasSelections && isOpen && (
        <div className="mt-4 flex justify-center w-full">
          <Button
            variant="outline"
            onClick={e => {
              e.stopPropagation();
              onEdit?.();
            }}
            className="rounded-full border border-border bg-app-background text-foreground hover:bg-app-surface-hover hover:text-foreground whitespace-nowrap px-6"
            data-test-id={`edit-picks-${id}`}
          >
            {t('game.actions.editPicks')}
            <Pencil className="w-4 h-4 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default GameCard;
